import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, careersAPI } from '../../services/api';
import { useTheme } from '../../contexts/Themecontext';
import { useAuth } from '../../contexts/Authcontext';
import { 
  User, Mail, Briefcase, Clock, CheckCircle, XCircle, 
  ShieldCheck, ExternalLink, LogOut, Settings, X, ShieldAlert,
  MapPin, FileText, Download, Eye, AlertCircle, Trash2,
  Calendar, DollarSign, Building
} from 'lucide-react';

const StudentDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 2FA States
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const { theme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [profileData, appsData] = await Promise.all([
        authAPI.getProfile(),
        careersAPI.getMyApplications()
      ]);
      setProfile(profileData);
      setApplications(appsData || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEnable2FA = async () => {
    try {
      const response = await authAPI.enable2FA();
      setSecret(response.secret);
      setQrCode(response.qr_code);
      setShow2FAModal(true);
    } catch (err) {
      alert('Failed to enable 2FA');
    }
  };

  const handleDisable2FA = async () => {
    if (window.confirm("Are you sure you want to disable 2FA?")) {
      try {
        await authAPI.disable2FA();
        fetchDashboardData();
      } catch (err) {
        alert('Failed to disable 2FA');
      }
    }
  };

  const handleToggle2FA = async () => {
    if (profile?.two_factor_enabled) {
      handleDisable2FA();
    } else {
      handleEnable2FA();
    }
  };

  const verifyAndEnable2FA = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      await authAPI.verify2FA(otp);
      setShow2FAModal(false);
      setOtp('');
      fetchDashboardData();
      alert('2FA enabled successfully!');
    } catch (err) {
      alert('Invalid verification code');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleViewDetails = (app) => {
    setSelectedApp(app);
    setShowDetailModal(true);
  };

  const handleWithdrawApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) {
      return;
    }

    try {
      await careersAPI.withdrawApplication(appId);
      alert('Application withdrawn successfully');
      fetchDashboardData();
      setShowDetailModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to withdraw application');
    }
  };

  const getThemeClasses = () => {
    const isDark = theme === 'dark' || theme === 'quantum';
    return {
      bg: theme === 'light' ? 'bg-gray-50' : theme === 'dark' ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950',
      card: theme === 'light' ? 'bg-white border-gray-200 text-gray-900' : 'bg-slate-900/70 backdrop-blur-xl border-white/10 text-white',
      text: isDark ? 'text-white' : 'text-gray-900',
      textMuted: isDark ? 'text-gray-400' : 'text-gray-500',
      accent: theme === 'quantum' ? 'text-purple-400' : theme === 'dark' ? 'text-cyan-400' : 'text-blue-600',
      btnPrimary: theme === 'quantum' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : theme === 'dark' ? 'bg-cyan-600' : 'bg-blue-600',
      border: isDark ? 'border-white/10' : 'border-gray-200',
    };
  };

  const classes = getThemeClasses();

  if (loading) {
    return (
      <div className={`min-h-screen ${classes.bg} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={classes.textMuted}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${classes.bg} p-4 md:p-8 pt-24 md:pt-32 transition-all duration-500`}>
      <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
        
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 flex items-center gap-3">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={fetchDashboardData} className="ml-auto px-4 py-2 bg-red-500/20 rounded-lg hover:bg-red-500/30">
              Retry
            </button>
          </div>
        )}
        
        {/* Profile Header */}
        <div className={`${classes.card} p-6 md:p-10 rounded-2xl md:rounded-3xl border shadow-2xl`}>
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="relative group">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl md:rounded-3xl bg-gradient-to-tr from-cyan-500 via-purple-600 to-pink-600 p-1">
                <div className={`w-full h-full rounded-2xl md:rounded-[1.4rem] ${classes.card} flex items-center justify-center text-3xl md:text-5xl font-bold`}>
                  {profile?.profile_picture ? (
                    <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover rounded-2xl md:rounded-[1.4rem]" />
                  ) : (
                    <span>{profile?.first_name?.[0]}{profile?.last_name?.[0]}</span>
                  )}
                </div>
              </div>
              {profile?.two_factor_enabled && (
                <div className="absolute -bottom-2 -right-2 bg-green-500 p-2 rounded-full border-4 border-slate-900 shadow-lg">
                  <ShieldCheck size={20} className="text-white" />
                </div>
              )}
            </div>
            
            <div className="flex-1 text-center md:text-left space-y-2">
              <h1 className="text-3xl md:text-5xl font-black tracking-tighter">{profile?.first_name} {profile?.last_name}</h1>
              <p className={`${classes.textMuted} flex items-center justify-center md:justify-start gap-2 flex-wrap`}>
                <Mail size={18} /> {profile?.email}
              </p>
              {profile?.phone && (
                <p className={`${classes.textMuted} text-sm`}>📱 {profile.phone}</p>
              )}
            </div>

            <div className="flex flex-row md:flex-col gap-3 w-full md:w-auto">
              <Link to="/profile/edit" className="flex-1 md:flex-none px-6 py-3 rounded-xl md:rounded-2xl font-bold flex items-center justify-center gap-2 border ${classes.border} hover:bg-white/5 transition-all">
                <Settings size={18} /> Edit
              </Link>
              <button 
                onClick={handleLogout}
                className="flex-1 md:flex-none px-6 py-3 rounded-xl md:rounded-2xl font-bold bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-all flex items-center justify-center gap-2"
              >
                <LogOut size={18} /> Logout
              </button>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          
          {/* Applications Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <Briefcase className={classes.accent} size={28} /> 
                My Applications
              </h2>
              <Link to="/careers" className={`px-4 py-2 rounded-xl ${classes.btnPrimary} text-white font-bold text-sm hover:opacity-90 transition-all`}>
                Browse Jobs
              </Link>
            </div>
            
            <div className="grid gap-4">
              {applications.length > 0 ? (
                applications.map((app) => (
                  <div key={app.id || app._id} className={`${classes.card} p-5 md:p-6 rounded-xl md:rounded-2xl border hover:border-purple-500/50 transition-all group`}>
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex-1 space-y-3 w-full">
                        <div className="flex items-start justify-between gap-4">
                          <h3 className="text-lg md:text-xl font-bold">{app.job_title}</h3>
                          <StatusBadge status={app.status} />
                        </div>
                        
                        <div className={`flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm ${classes.textMuted}`}>
                          <span className="flex items-center gap-1">
                            <Clock size={14}/> Applied {new Date(app.created_at).toLocaleDateString()}
                          </span>
                          {app.updated_at !== app.created_at && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14}/> Updated {new Date(app.updated_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {app.notes && (
                          <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                            <p className="text-sm"><strong>Recruiter Note:</strong> {app.notes}</p>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 pt-2">
                          <button 
                            onClick={() => handleViewDetails(app)}
                            className="px-4 py-2 bg-purple-500/10 text-purple-500 rounded-lg hover:bg-purple-500/20 transition-all flex items-center gap-2 text-sm font-bold"
                          >
                            <Eye size={16} /> View Details
                          </button>
                          {app.resume_url && (
                            <a 
                              href={app.resume_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-500/10 text-blue-500 rounded-lg hover:bg-blue-500/20 transition-all flex items-center gap-2 text-sm font-bold"
                            >
                              <Download size={16} /> Resume
                            </a>
                          )}
                          {(app.status === 'applied' || app.status === 'screening') && (
                            <button
                              onClick={() => handleWithdrawApplication(app.id || app._id)}
                              className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-all flex items-center gap-2 text-sm font-bold"
                            >
                              <Trash2 size={16} /> Withdraw
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={`${classes.card} p-8 md:p-12 rounded-2xl md:rounded-3xl border-dashed border-2 text-center`}>
                  <Briefcase size={48} className={`${classes.textMuted} mx-auto mb-4`} />
                  <p className={`text-lg font-bold ${classes.text} mb-2`}>No Applications Yet</p>
                  <p className={classes.textMuted}>Start applying to open positions!</p>
                  <Link to="/careers" className={`mt-4 inline-block px-6 py-3 rounded-xl ${classes.btnPrimary} text-white font-bold hover:opacity-90 transition-all`}>
                    Browse Jobs
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Security & Stats Sidebar */}
          <div className="space-y-6">
            {/* Security Card */}
            <div>
              <h2 className="text-xl md:text-2xl font-bold flex items-center gap-3 mb-4">
                <ShieldCheck className={classes.accent} size={24} /> Security
              </h2>
              
              <div className={`${classes.card} p-6 rounded-2xl border space-y-6`}>
                <div className="flex justify-between items-center group cursor-pointer" onClick={handleToggle2FA}>
                  <div className="space-y-1">
                    <p className="font-bold">Two-Factor Auth</p>
                    <p className={`text-xs ${classes.textMuted}`}>Extra security layer</p>
                  </div>
                  <div className={`w-14 h-7 rounded-full p-1 transition-colors ${profile?.two_factor_enabled ? 'bg-green-500' : 'bg-slate-700'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-lg transition-transform ${profile?.two_factor_enabled ? 'translate-x-7' : 'translate-x-0'}`} />
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/5 border border-white/5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold uppercase ${classes.textMuted}`}>Account Role</span>
                    <span className={`text-sm font-mono ${classes.accent}`}>{profile?.role}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold uppercase ${classes.textMuted}`}>Status</span>
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" /> Active
                    </span>
                  </div>
                  {profile?.last_login && (
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold uppercase ${classes.textMuted}`}>Last Login</span>
                      <span className="text-xs">{new Date(profile.last_login).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className={`${classes.card} p-6 rounded-2xl border`}>
              <h3 className="font-bold mb-4">Application Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={classes.textMuted}>Total Applied</span>
                  <span className="text-xl font-bold">{applications.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={classes.textMuted}>In Progress</span>
                  <span className="text-xl font-bold text-yellow-500">
                    {applications.filter(a => a.status === 'screening' || a.status === 'interview').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={classes.textMuted}>Offers</span>
                  <span className="text-xl font-bold text-green-500">
                    {applications.filter(a => a.status === 'offer').length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className={`${classes.card} w-full max-w-md p-8 rounded-3xl border shadow-2xl relative`}>
            <button onClick={() => setShow2FAModal(false)} className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full">
              <X size={20}/>
            </button>
            
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-purple-500/20 text-purple-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ShieldAlert size={32} />
              </div>
              <h3 className="text-2xl font-black">Enable 2FA</h3>
              <p className={classes.textMuted}>Scan this QR code with your authenticator app</p>
              
              {qrCode && (
                <div className="p-4 bg-white rounded-xl">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48 mx-auto" />
                </div>
              )}

              <div className="p-3 bg-black/20 rounded-lg">
                <p className="text-xs font-mono break-all">{secret}</p>
              </div>
              
              <form onSubmit={verifyAndEnable2FA} className="space-y-4 pt-4">
                <input 
                  type="text" 
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full bg-black/20 border border-white/10 rounded-xl py-4 text-center text-2xl font-mono tracking-widest focus:border-purple-500 outline-none"
                  maxLength={6}
                  required
                />
                <button 
                  type="submit"
                  disabled={isVerifying || otp.length !== 6}
                  className={`w-full py-4 rounded-xl font-bold ${classes.btnPrimary} text-white shadow-xl hover:opacity-90 transition-opacity disabled:opacity-50`}
                >
                  {isVerifying ? "Verifying..." : "Verify & Enable"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Application Detail Modal */}
      {showDetailModal && selectedApp && (
        <ApplicationDetailModal 
          app={selectedApp}
          classes={classes}
          onClose={() => setShowDetailModal(false)}
          onWithdraw={handleWithdrawApplication}
        />
      )}
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const configs = {
    applied: { color: 'bg-blue-500/10 text-blue-500 border-blue-500/20', icon: <Clock size={12}/>, label: 'Applied' },
    screening: { color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', icon: <User size={12}/>, label: 'Screening' },
    interview: { color: 'bg-purple-500/10 text-purple-500 border-purple-500/20', icon: <Calendar size={12}/>, label: 'Interview' },
    offer: { color: 'bg-green-500/10 text-green-500 border-green-500/20', icon: <CheckCircle size={12}/>, label: 'Offer' },
    rejected: { color: 'bg-red-500/10 text-red-500 border-red-500/20', icon: <XCircle size={12}/>, label: 'Rejected' },
    withdrawn: { color: 'bg-gray-500/10 text-gray-500 border-gray-500/20', icon: <XCircle size={12}/>, label: 'Withdrawn' },
  };
  const config = configs[status?.toLowerCase()] || configs.applied;
  return (
    <span className={`px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-black uppercase tracking-wider border flex items-center gap-1.5 ${config.color} whitespace-nowrap`}>
      {config.icon} {config.label}
    </span>
  );
};

// Application Detail Modal Component
const ApplicationDetailModal = ({ app, classes, onClose, onWithdraw }) => {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl overflow-y-auto">
      <div className={`${classes.card} w-full max-w-4xl rounded-3xl border shadow-2xl my-8`}>
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-white/10 flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-black mb-2">{app.job_title}</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="flex items-center gap-1">
                <Calendar size={14}/> Applied {new Date(app.created_at).toLocaleDateString()}
              </span>
              {app.updated_at !== app.created_at && (
                <span className="flex items-center gap-1">
                  <Clock size={14}/> Updated {new Date(app.updated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-all">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Status */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 opacity-60">Current Status</h3>
            <StatusBadge status={app.status} />
          </div>

          {/* Applicant Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoItem icon={User} label="Name" value={`${app.first_name} ${app.last_name}`} />
            <InfoItem icon={Mail} label="Email" value={app.email} />
            {app.phone && <InfoItem icon="📱" label="Phone" value={app.phone} />}
            {app.years_experience && <InfoItem icon={Briefcase} label="Experience" value={`${app.years_experience} years`} />}
          </div>

          {/* Cover Letter */}
          {app.cover_letter && (
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3 opacity-60">Cover Letter</h3>
              <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                <p className="text-sm leading-relaxed italic">"{app.cover_letter}"</p>
              </div>
            </div>
          )}

          {/* Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {app.resume_url && (
              <a href={app.resume_url} target="_blank" rel="noopener noreferrer" className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl hover:bg-blue-500/20 transition-all flex items-center justify-between group">
                <span className="font-bold text-blue-500">Resume</span>
                <ExternalLink size={18} className="text-blue-500 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
            {app.linkedin_url && (
              <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl hover:bg-purple-500/20 transition-all flex items-center justify-between group">
                <span className="font-bold text-purple-500">LinkedIn</span>
                <ExternalLink size={18} className="text-purple-500 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
            {app.portfolio_url && (
              <a href={app.portfolio_url} target="_blank" rel="noopener noreferrer" className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl hover:bg-green-500/20 transition-all flex items-center justify-between group">
                <span className="font-bold text-green-500">Portfolio</span>
                <ExternalLink size={18} className="text-green-500 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
          </div>

          {/* Notes */}
          {app.notes && (
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3 opacity-60">Recruiter Notes</h3>
              <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
                <p className="text-sm">{app.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {(app.status === 'applied' || app.status === 'screening') && (
          <div className="p-6 md:p-8 border-t border-white/10">
            <button 
              onClick={() => onWithdraw(app.id || app._id)}
              className="w-full py-3 bg-red-500/10 text-red-500 border border-red-500/20 rounded-xl hover:bg-red-500 hover:text-white transition-all font-bold flex items-center justify-center gap-2"
            >
              <Trash2 size={18} /> Withdraw Application
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Info Item Component
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="p-4 bg-white/5 rounded-xl border border-white/5">
    <div className="flex items-center gap-2 mb-1 opacity-60">
      {typeof Icon === 'string' ? <span>{Icon}</span> : <Icon size={14} />}
      <span className="text-xs font-bold uppercase">{label}</span>
    </div>
    <p className="font-bold">{value}</p>
  </div>
);

export default StudentDashboard;